import { useTranslation } from "react-i18next";

const stats = [
  { value: "100 GW", labelKey: "spx_stat1_label", subKey: "spx_stat1_sub", icon: "ri-flashlight-fill" },
  { value: "×5", labelKey: "spx_stat2_label", subKey: "spx_stat2_sub", icon: "ri-sun-fill" },
  { value: "30", labelKey: "spx_stat3_label", subKey: "spx_stat3_sub", icon: "ri-time-fill" },
  { value: "99%", labelKey: "spx_stat4_label", subKey: "spx_stat4_sub", icon: "ri-signal-wifi-fill" },
];

const facts = [
  { icon: "ri-rocket-2-line", titleKey: "spx_fact1_title", descKey: "spx_fact1_desc" },
  { icon: "ri-sun-line", titleKey: "spx_fact2_title", descKey: "spx_fact2_desc" },
  { icon: "ri-global-line", titleKey: "spx_fact3_title", descKey: "spx_fact3_desc" },
];

export default function SpaceXSection() {
  const { t } = useTranslation();

  return (
    <section className="relative py-24 md:py-32 bg-[#060a12] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f5775b8474c40ed500344ead925f1d97.jpg"
          alt="SpaceX solar deployment"
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060a12] via-[#060a12]/70 to-[#060a12]" />
      </div>

      {/* Animated grid */}
      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "linear-gradient(rgba(255,200,50,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(255,200,50,0.2) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-amber-400/60" />
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {t("spx_badge")}
            </span>
            <div className="w-8 h-px bg-amber-400/60" />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {t("spx_title_1")}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
              {t("spx_title_2")}
            </span>
          </h2>
          <p className="text-white/50 text-sm max-w-2xl mx-auto leading-relaxed">{t("spx_subtitle")}</p>
        </div>

        {/* SpaceX logo strip */}
        <div className="flex items-center justify-center gap-4 mb-14">
          <div className="h-px flex-1 bg-white/10" />
          <div className="flex items-center gap-3 px-6 py-3 border border-white/10 rounded-sm bg-white/[0.03]">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-rocket-2-fill text-white text-lg" />
            </div>
            <span className="text-white font-black text-lg tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              SpaceX
            </span>
            <span className="text-white/30 text-xs">×</span>
            <span className="text-amber-400 font-black text-lg tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              ARTEMIDA
            </span>
          </div>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* Key stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {stats.map((s, i) => (
            <div key={i} className="p-6 border border-amber-400/20 rounded-lg bg-amber-400/[0.03] text-center hover:border-amber-400/40 hover:bg-amber-400/[0.06] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/20 mx-auto mb-3">
                <i className={`${s.icon} text-amber-400`} />
              </div>
              <div className="text-3xl md:text-4xl font-black text-amber-400 mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                {s.value}
              </div>
              <div className="text-white text-xs font-semibold mb-1 tracking-wide">{t(s.labelKey)}</div>
              <div className="text-white/30 text-xs leading-relaxed">{t(s.subKey)}</div>
            </div>
          ))}
        </div>

        {/* Main content: image + facts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative rounded-lg overflow-hidden h-[420px]">
            <img
              src="https://readdy.ai/api/search-image?query=Elon%20Musk%20SpaceX%20Starship%20massive%20rocket%20on%20launch%20pad%20at%20sunset%2C%20dramatic%20cinematic%20photography%2C%20industrial%20scale%2C%20orange%20and%20golden%20sky%2C%20photorealistic%2C%20epic%20composition%2C%20ultra%20high%20resolution%2C%20dark%20moody%20atmosphere&width=800&height=700&seq=spx002&orientation=portrait"
              alt="SpaceX Starship"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060a12] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#060a12]/50" />

            {/* Floating quote card */}
            <div className="absolute bottom-6 left-6 right-6 p-5 bg-black/80 backdrop-blur-md border border-amber-400/20 rounded-lg">
              <i className="ri-double-quotes-l text-amber-400/40 text-2xl mb-2 block" />
              <p className="text-white/80 text-sm italic leading-relaxed">{t("spx_quote")}</p>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10">
                  <i className="ri-user-line text-white/60 text-xs" />
                </div>
                <span className="text-amber-400 text-xs font-bold tracking-widest" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  Elon Musk — SpaceX CEO
                </span>
              </div>
            </div>
          </div>

          {/* Facts */}
          <div className="space-y-6">
            <p className="text-white/60 text-sm leading-relaxed border-l-2 border-amber-400/40 pl-4">
              {t("spx_intro")}
            </p>
            {facts.map((f, i) => (
              <div key={i} className="flex gap-4 p-5 border border-white/10 rounded-lg bg-white/[0.02] hover:border-amber-400/20 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-400/10 border border-amber-400/20 flex-shrink-0">
                  <i className={`${f.icon} text-amber-400`} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>{t(f.titleKey)}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{t(f.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Efficiency comparison bar */}
        <div className="p-8 border border-white/10 rounded-lg bg-white/[0.02]">
          <h3 className="text-white font-bold text-sm mb-6 tracking-widest uppercase text-center" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {t("spx_compare_title")}
          </h3>
          <div className="space-y-5">
            {[
              { labelKey: "spx_compare_ground", pct: 20, color: "bg-white/30", value: "~20%" },
              { labelKey: "spx_compare_orbit", pct: 100, color: "bg-gradient-to-r from-amber-400 to-orange-500", value: "~99% × ×5 КПД" },
            ].map((item) => (
              <div key={item.labelKey} className="flex items-center gap-4">
                <div className="w-48 text-white/50 text-xs text-right whitespace-nowrap">{t(item.labelKey)}</div>
                <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${item.pct}%` }} />
                </div>
                <div className="w-28 text-amber-400 text-xs font-bold" style={{ fontFamily: "'Orbitron', sans-serif" }}>{item.value}</div>
              </div>
            ))}
          </div>
          <p className="text-white/30 text-xs text-center mt-6 leading-relaxed">{t("spx_compare_note")}</p>
        </div>
      </div>
    </section>
  );
}
